
import { Component, Input, OnChanges, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { DEFAULT_EXTRACTION_CONFIG, ExtractionConfig } from '@shared/index';
import { TreeNode } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { TreeModule } from 'primeng/tree';
import { SelectableDocument } from '../../services/file-upload.service';
import { ProjectDocumentsService } from '../../services/project-documents.service';

@Component({
    selector: 'app-extraction-dialog',
    templateUrl: './extraction-dialog.component.html',
    styleUrl: './extraction-dialog.component.scss',
    standalone: true,
    imports: [
        FormsModule,
        DialogModule,
        ButtonModule,
        SelectModule,
        TreeModule,
        InputNumberModule,
        ToggleSwitchModule,
        ChipModule,
        TooltipModule
    ]
})
export class ExtractionDialogComponent implements OnChanges {
    public readonly doc = input<SelectableDocument | null>(null);
    @Input() public visible = false;
    public readonly visibleChange = output<boolean>();
    public readonly extracted = output();

    public extractionConfig: ExtractionConfig = { ...DEFAULT_EXTRACTION_CONFIG };
    public extractionValidationAttempted = false;
    public extractionLoading = false;
    public ocrLanguagesSelected: string[] = ['eng'];

    public readonly chunkingStrategyOptions = [
        {
            label: $localize`:@@extraction-chunking-text:Text`,
            value: 'text' as const,
            subtitle: $localize`:@@extraction-chunking-text-subtitle:Trennung nach Leerzeichen/Satzzeichen`
        },
        {
            label: $localize`:@@extraction-chunking-markdown:Markdown`,
            value: 'markdown' as const,
            subtitle: $localize`:@@extraction-chunking-markdown-subtitle:Strukturbewusst; behält Überschriften, Listen und Codeblöcke bei`
        },
        {
            label: $localize`:@@extraction-chunking-yaml:YAML`,
            value: 'yaml' as const,
            subtitle: $localize`:@@extraction-chunking-yaml-subtitle:Abschnittsbewusst; behält die Struktur des YAML-Dokuments bei`
        }
    ];

    public readonly extractionParameterTree: TreeNode[] = [
        {
            key: 'chunk-parameters',
            label: $localize`:@@extraction-chunk-parameters: Fortgeschrittene Einstellungen`,
            expanded: false,
            selectable: false,
            children: [
                { key: 'max-chars', selectable: false },
                { key: 'max-overlap', selectable: false }
            ]
        }
    ];

    public readonly ocrLanguageOptions = [
        { label: 'Arabisch (ara)', value: 'ara' },
        { label: 'Chinesisch vereinfacht (chi_sim)', value: 'chi_sim' },
        { label: 'Chinesisch traditionell (chi_tra)', value: 'chi_tra' },
        { label: 'Deutsch (deu)', value: 'deu' },
        { label: 'Englisch (eng)', value: 'eng' },
        { label: 'Französisch (fra)', value: 'fra' },
        { label: 'Hindi (hin)', value: 'hin' },
        { label: 'Italienisch (ita)', value: 'ita' },
        { label: 'Japanisch (jpn)', value: 'jpn' },
        { label: 'Orientierungserkennung (osd)', value: 'osd' },
        { label: 'Portugiesisch (por)', value: 'por' },
        { label: 'Russisch (rus)', value: 'rus' },
        { label: 'Spanisch (spa)', value: 'spa' }
    ];

    constructor(
        private projectDocumentsService: ProjectDocumentsService,
        private toastr: ToastrService
    ) { }

    public ngOnChanges(): void {
        const doc = this.doc();
        if (this.visible && doc) {
            this.extractionConfig = { ...(doc.extractionConfig ?? DEFAULT_EXTRACTION_CONFIG) };
            this.extractionValidationAttempted = false;
            this.ocrLanguagesSelected = this.extractionConfig.ocrLanguage
                ? this.extractionConfig.ocrLanguage.split('+')
                : ['eng'];
        }
    }

    public close(): void {
        this.visible = false;
        this.visibleChange.emit(false);
    }

    public toggleExtractionParameterTree(node: TreeNode): void {
        if (node.key !== 'chunk-parameters') return;
        node.expanded = !node.expanded;
    }

    /* eslint-disable @typescript-eslint/no-unnecessary-condition */
    public isExtractionMaxCharsInvalid(): boolean {
        return this.extractionValidationAttempted
            && (this.extractionConfig.maxChars === null || this.extractionConfig.maxChars === undefined);
    }

    public isExtractionMaxOverlapInvalid(): boolean {
        return this.extractionValidationAttempted
            && (this.extractionConfig.maxOverlap === null || this.extractionConfig.maxOverlap === undefined);
    }
    /* eslint-enable @typescript-eslint/no-unnecessary-condition */

    public isExtractionChunkingStrategyInvalid(): boolean {
        return this.extractionValidationAttempted && !this.extractionConfig.chunking_strategy;
    }

    private hasRequiredExtractionFields(): boolean {
        /* eslint-disable @typescript-eslint/no-unnecessary-condition */
        return this.extractionConfig.maxChars !== null
            && this.extractionConfig.maxChars !== undefined
            && this.extractionConfig.maxOverlap !== null
            && this.extractionConfig.maxOverlap !== undefined
            /* eslint-enable @typescript-eslint/no-unnecessary-condition */
            && !!this.extractionConfig.chunking_strategy;
    }

    public getOcrLanguageLabel(value: string): string {
        return this.ocrLanguageOptions.find(o => o.value === value)?.label ?? value;
    }

    public removeOcrLanguage(event: Event, value: string): void {
        event.stopPropagation();
        this.ocrLanguagesSelected = this.ocrLanguagesSelected.filter(v => v !== value);
    }

    public async triggerExtraction(): Promise<void> {
        const doc = this.doc();
        if (!doc) return;

        this.extractionValidationAttempted = true;
        if (!this.hasRequiredExtractionFields()) return;

        this.extractionConfig.ocrLanguage = this.ocrLanguagesSelected.length > 0
            ? this.ocrLanguagesSelected.join('+')
            : 'eng';
        this.extractionLoading = true;

        const result = await this.projectDocumentsService.triggerExtraction(
            doc._id,
            this.extractionConfig
        );

        this.extractionLoading = false;

        if (result && result.status === 'ok') {
            this.toastr.success('GENERAL', $localize`:@@extraction-success:Extraktion abgeschlossen`);
            this.close();
            // TODO: The 'emit' function requires a mandatory void argument
            this.extracted.emit();
        } else {
            const errorType = result?.errorType;
            const hint = this.getExtractionErrorMessage(errorType, result?.message);
            const body = this.buildToastBody(hint, errorType ? result.message : undefined);
            this.toastr.error('GENERAL', body, { enableHtml: true });
        }
    }

    private buildToastBody(hint: string, rawMessage?: string): string {
        if (!rawMessage) return hint;
        const escaped = rawMessage.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        return `${hint}<br><small class="opacity-75 d-block mt-1">${escaped}</small>`;
    }

    private getExtractionErrorMessage(errorType?: string, fallbackMsg?: string): string {
        switch (errorType) {
            case 'ParsingError':
                return $localize`:@@extraction-error-parsing:Das Dokument konnte nicht gelesen werden. Prüfe, ob das Format korrekt ist.`;
            case 'InternalError':
                return $localize`:@@extraction-error-internal:Ein interner Fehler ist aufgetreten. Versuche es erneut.`;
            case 'ExtractionError':
                return $localize`:@@extraction-error-extraction:Die Inhalte konnten nicht extrahiert werden. Prüfe das Dokument und versuche es erneut.`;
            default:
                return fallbackMsg ?? $localize`:@@extraction-error:Extraktion fehlgeschlagen. Versuche es erneut.`;
        }
    }
}
